import axios from 'axios';

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // Always use production API for deployed apps
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return 'https://api.trackyourwheelz.com/api';
  }
  
  // For local development, you can change this if needed
  return process.env.REACT_APP_API_BASE_URL || 'https://api.trackyourwheelz.com/api';
};

// Create axios instance
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false, // Set to true if backend requires credentials
});

// Request interceptor - adds JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log request details for debugging - with clear formatting
    console.group(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    console.log('📍 Full URL:', config.baseURL + config.url);
    console.log('🔧 Method:', config.method?.toUpperCase());
    console.log('📦 Payload:', config.data);
    console.log('📋 Headers:', {
      'Content-Type': config.headers['Content-Type'],
      'Accept': config.headers['Accept'],
      'Authorization': config.headers.Authorization ? '✓ Present' : '✗ None'
    });
    console.log('⏰ Timestamp:', new Date().toLocaleTimeString());
    console.groupEnd();
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handles token expiration and errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses
    console.group(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    console.log('📊 Status:', response.status, response.statusText);
    console.log('📦 Data:', response.data);
    console.log('⏰ Timestamp:', new Date().toLocaleTimeString());
    console.groupEnd();
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint =
      requestUrl.includes('/login') ||
      requestUrl.includes('/refresh') ||
      requestUrl.includes('/customer-account/register');

    // If token is expired (401 error) and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${getApiBaseUrl()}/refresh`, {
            refreshToken: refreshToken
          });

          const { token: newToken } = response.data;
          localStorage.setItem('jwtToken', newToken);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // No refresh token available, clear all auth data and redirect to login
          localStorage.removeItem('jwtToken');
          localStorage.removeItem('user');
          localStorage.removeItem('loggedAs');
          localStorage.removeItem('userId');
          localStorage.removeItem('userName');
          delete axios.defaults.headers.common['Authorization'];
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // If refresh fails, logout user and clear all auth data
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('loggedAs');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        delete axios.defaults.headers.common['Authorization'];
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    if (error.response?.status === 403) {
      const isLogoutRequest = originalRequest?.url?.includes('/logout');

      if (!isLogoutRequest) {
        console.error('Access forbidden');
      }
    }

    // Handle CORS errors
    if (error.code === 'ERR_NETWORK' || !error.response) {
      console.error('Network Error - Possible CORS issue or server is down');
      console.error('Request URL:', error.config?.url);
      console.error('Request Method:', error.config?.method);
      console.error('Full error:', error);
      
      // Create a more user-friendly error
      const corsError = new Error(
        'Unable to connect to the server. This might be a CORS configuration issue or the server might be down. ' +
        'Please check: 1) Backend CORS settings, 2) API URL is correct, 3) Server is running'
      );
      corsError.isCorsError = true;
      corsError.originalError = error;
      return Promise.reject(corsError);
    }

    return Promise.reject(error);
  }
);

export default api;
